/*
 * Контуры: капли поля ввода, которые сливаются шейкой (`path`), и
 * скруглённый прямоугольник с хвостиком (`rrect`) — для слитого поля и
 * подсказки. Формулы — из `liquid-glass.js` макета v6, дословно; само стекло
 * (преломление, блик, карты) убрано 3 ноября, контуры остались.
 */

export type Span = [number, number];

/** Отросток капли: тянется к соседу перед слиянием и пружинит после разрыва. */
export interface Nub {
  u: number;
  vu: number;
  e: number;
  soft?: boolean;
}

/** Носик подсказки: часть контура, поэтому кромка и тень идут по нему же. */
export interface Pip {
  side: "top" | "right" | "bottom" | "left";
  at: number;
  w: number;
  h: number;
}

type Nubs = (Nub | null | undefined)[];

const f2 = (n: number) => n.toFixed(2);

/*
 * Контур капель одной высоты. Соседи ближе `G` сливаются шейкой, отростки
 * (`nubs`) тянут край к соседу. `gs` — вздутие нажатой капли: рисуется
 * наружу, но шейка и слияние считаются по невздутому зазору, чтобы нажатие
 * никогда не перекидывало мост к соседу.
 */
export function path(
  items: Span[],
  h: number,
  G: number,
  oy: number,
  nubs: Nubs,
  K: number,
  pow: number,
  gs?: number[],
): string {
  // Налезающие капли (кнопка рождается из родителя или складывается в него) рисуются объединением.
  if (items.length > 1) {
    const U: Span[] = [];
    const UN: Nubs = [];
    const UG: number[] = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const s = gs ? gs[i] || 0 : 0;
      const last = U[U.length - 1];
      if (last && it[0] - last[1] <= 0) {
        last[0] = Math.min(last[0], it[0]);
        last[1] = Math.max(last[1], it[1]);
        UG[UG.length - 1] = Math.max(UG[UG.length - 1], s);
      } else {
        if (U.length) UN.push(nubs[i - 1]);
        U.push([it[0], it[1]]);
        UG.push(s);
      }
    }
    if (U.length < items.length) {
      items = U;
      nubs = UN;
      if (gs) gs = UG;
    }
  }
  const gap = (i: number, j: number) =>
    items[j][0] + (gs ? gs[j] || 0 : 0) - (items[i][1] - (gs ? gs[i] || 0 : 0));
  const r = h / 2;
  const T = oy || 0;
  const B = T + h;
  const cy = T + r;
  const neckY = (t: number) => r * K * (1 - Math.pow(1 - t, pow));
  const groups: number[][] = [];
  let cur = [0];
  for (let i = 1; i < items.length; i++) {
    if (gap(i - 1, i) < G) cur.push(i);
    else {
      groups.push(cur);
      cur = [i];
    }
  }
  groups.push(cur);
  const pts = (nb: Nub) => {
    const k = 0.5523 * r;
    if (nb.u < 0) {
      const s = Math.min(0.55, -nb.u);
      return { p1: k * (1 + s * 0.35), p2x: r * (1 - s * 0.22), p2y: k * (1 + s * 0.55), p3x: r * (1 - s * 0.4), th: 0 };
    }
    const u = Math.min(1, nb.u);
    const th0 = r * (1 - K);
    const m = r + nb.e;
    const L = (A: number, Bv: number) => A + (Bv - A) * u;
    return { p1: L(k, 0.7 * r), p2x: L(r, m - m * 0.45), p2y: L(k, th0), p3x: L(r, m - th0), th: u * th0 };
  };
  const capR = (cx: number, nb: Nub | null | undefined) => {
    if (!nb) return `A${f2(r)},${f2(r)} 0 0 1 ${f2(cx)},${f2(B)}`;
    const q = pts(nb);
    const x3 = cx + q.p3x;
    const t = q.th;
    return (
      `C${f2(cx + q.p1)},${f2(T)} ${f2(cx + q.p2x)},${f2(cy - q.p2y)} ${f2(x3)},${f2(cy - t)}` +
      `C${f2(x3 + 1.33 * t)},${f2(cy - t)} ${f2(x3 + 1.33 * t)},${f2(cy + t)} ${f2(x3)},${f2(cy + t)}` +
      `C${f2(cx + q.p2x)},${f2(cy + q.p2y)} ${f2(cx + q.p1)},${f2(B)} ${f2(cx)},${f2(B)}`
    );
  };
  const capL = (cx: number, nb: Nub | null | undefined) => {
    if (!nb) return `A${f2(r)},${f2(r)} 0 0 1 ${f2(cx)},${f2(T)}`;
    const q = pts(nb);
    const x3 = cx - q.p3x;
    const t = q.th;
    return (
      `C${f2(cx - q.p1)},${f2(B)} ${f2(cx - q.p2x)},${f2(cy + q.p2y)} ${f2(x3)},${f2(cy + t)}` +
      `C${f2(x3 - 1.33 * t)},${f2(cy + t)} ${f2(x3 - 1.33 * t)},${f2(cy - t)} ${f2(x3)},${f2(cy - t)}` +
      `C${f2(cx - q.p2x)},${f2(cy - q.p2y)} ${f2(cx - q.p1)},${f2(T)} ${f2(cx)},${f2(T)}`
    );
  };
  const ex = (i: number) => Math.max(items[i][1] - r, items[i][0] + r);
  let d = "";
  groups.forEach((gr) => {
    // Одинокая нажатая капля вздувается равномерно — и вширь, и ввысь — вокруг своего центра.
    const i0 = gr[0];
    const s0 = gs && gr.length === 1 ? gs[i0] || 0 : 0;
    if (s0 > 0.01 && !nubs[i0] && !nubs[i0 - 1]) {
      const l = items[i0][0];
      const rt = items[i0][1];
      const t = T - s0;
      const b = B + s0;
      const R = Math.min(r + s0, (rt - l) / 2);
      const a = l + R;
      const e = Math.max(rt - R, a);
      d += `M${f2(a)},${f2(t)}L${f2(e)},${f2(t)}A${f2(R)},${f2(R)} 0 0 1 ${f2(e)},${f2(b)}L${f2(a)},${f2(b)}A${f2(R)},${f2(R)} 0 0 1 ${f2(a)},${f2(t)}Z`;
      return;
    }
    const fi = gr[0];
    const la = gr[gr.length - 1];
    d += `M${f2(items[fi][0] + r)},${f2(T)}`;
    for (let k = 0; k < gr.length; k++) {
      const i = gr[k];
      d += `L${f2(ex(i))},${f2(T)}`;
      if (k < gr.length - 1) {
        const j = gr[k + 1];
        const ax = ex(i);
        const bx = items[j][0] + r;
        const t = Math.max(0, Math.min(1, gap(i, j) / G));
        const ny = T + neckY(t);
        const mid = (ax + bx) / 2;
        d += `C${f2(ax + r * 0.7)},${f2(T)} ${f2(mid - (mid - ax) * 0.45)},${f2(ny)} ${f2(mid)},${f2(ny)}C${f2(mid + (bx - mid) * 0.45)},${f2(ny)} ${f2(bx - r * 0.7)},${f2(T)} ${f2(bx)},${f2(T)}`;
      }
    }
    d += capR(ex(la), nubs[la]);
    for (let k = gr.length - 1; k >= 0; k--) {
      const i = gr[k];
      d += `L${f2(items[i][0] + r)},${f2(B)}`;
      if (k > 0) {
        const p = gr[k - 1];
        const px = ex(p);
        const ax = items[i][0] + r;
        const t = Math.max(0, Math.min(1, gap(p, i) / G));
        const ny = B - neckY(t);
        const mid = (px + ax) / 2;
        d += `C${f2(ax - r * 0.7)},${f2(B)} ${f2(mid + (ax - mid) * 0.45)},${f2(ny)} ${f2(mid)},${f2(ny)}C${f2(mid - (mid - px) * 0.45)},${f2(ny)} ${f2(px + r * 0.7)},${f2(B)} ${f2(px)},${f2(B)}`;
      }
    }
    d += capL(items[fi][0] + r, nubs[fi - 1]) + "Z";
  });
  return d;
}

/*
 * Скруглённый прямоугольник вместо капли — для слитого многострочного поля и
 * подсказок. `pip` (хвостик) вписан прямо в контур: по нему же идут вырез
 * материала, кромка и тень.
 */
export function rrect(items: Span[], h: number, oy: number, rad: number, pip?: Pip | null): string {
  const T = oy || 0;
  const B = T + h;
  let d = "";
  items.forEach(([l, r], idx) => {
    const k = Math.min(rad, h / 2, (r - l) / 2);
    const c = k * 0.4477;
    const P = idx === 0 ? pip : null;
    const seg = (ax: number, ay: number, dx: number, dy: number, len: number, side: Pip["side"]) => {
      let s = "";
      if (P && P.side === side && len > 2) {
        const nx = dy;
        const ny = -dx;
        const w = Math.min(P.w, len);
        const ph = (P.h * w) / P.w;
        const hor = side === "top" || side === "bottom";
        let sp = hor ? (l + P.at - ax) * dx : (T + P.at - ay) * dy;
        sp = Math.max(w / 2, Math.min(len - w / 2, sp));
        const pt = (u: number, v: number) => `${f2(ax + dx * u + nx * v)},${f2(ay + dy * u + ny * v)}`;
        s = `L${pt(sp - w / 2, 0)}C${pt(sp - w * 0.27, 0)} ${pt(sp - w * 0.12, ph)} ${pt(sp, ph)}C${pt(sp + w * 0.12, ph)} ${pt(sp + w * 0.27, 0)} ${pt(sp + w / 2, 0)}`;
      }
      return s + `L${f2(ax + dx * len)},${f2(ay + dy * len)}`;
    };
    d +=
      `M${f2(l + k)},${f2(T)}` +
      seg(l + k, T, 1, 0, r - l - 2 * k, "top") +
      `C${f2(r - c)},${f2(T)} ${f2(r)},${f2(T + c)} ${f2(r)},${f2(T + k)}` +
      seg(r, T + k, 0, 1, h - 2 * k, "right") +
      `C${f2(r)},${f2(B - c)} ${f2(r - c)},${f2(B)} ${f2(r - k)},${f2(B)}` +
      seg(r - k, B, -1, 0, r - l - 2 * k, "bottom") +
      `C${f2(l + c)},${f2(B)} ${f2(l)},${f2(B - c)} ${f2(l)},${f2(B - k)}` +
      seg(l, B - k, 0, -1, h - 2 * k, "left") +
      `C${f2(l)},${f2(T + c)} ${f2(l + c)},${f2(T)} ${f2(l + k)},${f2(T)}Z`;
  });
  return d;
}
